import React, { useEffect, useState } from 'react';
import GridViewIcon from '@mui/icons-material/GridView';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import VideocamIcon from '@mui/icons-material/Videocam';
import { Alert, Box, Card, CardActions, CardContent, CardMedia, Chip, Grid, Typography } from '@mui/material';
import { coursesApi } from '../../api/client';
import CourseCreateForm from '../forms/CourseCreateForm';

const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:4000';

function mediaUrl(key?: string | null) {
  if (!key) return null;
  if (key.startsWith('http')) return key;
  return `${apiBase}/uploads/${key}`;
}

const iconBtn = (backgroundColor: string): React.CSSProperties => ({
  width: 32,
  height: 32,
  padding: 0,
  backgroundColor,
  color: 'white',
  border: 'none',
  borderRadius: 4,
  cursor: 'pointer',
  lineHeight: 1,
});

function contentLabel(course: any) {
  const contentType = course.contentType || (course.videoKey && course.pdfKey ? 'both' : course.pdfKey ? 'pdf' : 'video');
  if (contentType === 'both') return 'Video & PDF';
  if (contentType === 'pdf') return 'PDF';
  return 'Video';
}

const CoursesPage: React.FC = () => {
  const [items, setItems] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);

  const load = async () => {
    try {
      setItems(await coursesApi.list());
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (creating || editing) {
    return (
      <CourseCreateForm
        course={editing}
        onCancel={() => {
          setCreating(false);
          setEditing(null);
        }}
        onCreated={async () => {
          setCreating(false);
          setEditing(null);
          await load();
        }}
      />
    );
  }

  return (
    <Box>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h3 style={{ margin: 0 }}>Courses ({items.length})</h3>
        <button type="button" onClick={() => setCreating(true)} style={{ padding: '0.5rem 1rem', backgroundColor: '#1976d2', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
          + Create New Course
        </button>
      </div>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {items.length === 0 && <Typography>No courses found.</Typography>}
      <Grid container spacing={3}>
        {items.map((course) => {
          const image = mediaUrl(course.imageKey);
          const kind = contentLabel(course);
          return (
            <Grid item xs={12} sm={6} md={4} key={course.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 2, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                {image ? (
                  <CardMedia component="img" image={image} alt={course.title} sx={{ height: 180, objectFit: 'cover', bgcolor: 'grey.200' }} />
                ) : (
                  <Box sx={{ height: 180, bgcolor: 'primary.light', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
                    <ImageIcon sx={{ fontSize: 60, opacity: 0.5 }} />
                  </Box>
                )}
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main', lineHeight: 1.3, mb: 1.5 }}>
                    {course.title}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.75, mb: 1.5, flexWrap: 'wrap' }}>
                    <Chip
                      size="small"
                      variant="outlined"
                      color="info"
                      label={kind}
                      icon={
                        kind === 'PDF' ? <PictureAsPdfIcon /> : kind === 'Video' ? <VideocamIcon /> : (
                          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', pl: 0.5 }}>
                            <VideocamIcon sx={{ fontSize: 16 }} />
                            <PictureAsPdfIcon sx={{ fontSize: 16 }} />
                          </Box>
                        )
                      }
                    />
                    {course.duration && <Chip size="small" variant="outlined" color="primary" label={course.duration} />}
                    {course.category && <Chip size="small" variant="outlined" color="secondary" label={course.category} />}
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Passing Score:</strong> {course.passingScore ?? 'Not set'}%
                    </Typography>
                    {course.randomizeQuestions && (
                      <Typography variant="body2" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <ShuffleIcon sx={{ fontSize: 18, color: 'warning.main' }} />
                        <strong>Questions Randomized</strong>
                      </Typography>
                    )}
                    {course.randomizeOptions && (
                      <Typography variant="body2" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <ShuffleIcon sx={{ fontSize: 18, color: 'warning.main' }} />
                        <strong>Options Randomized</strong>
                      </Typography>
                    )}
                    {course.useQuestionPool && (
                      <Typography variant="body2" color="success.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <GridViewIcon sx={{ fontSize: 18 }} />
                        <strong>Question Pool: {course.questionsToDisplay}/{course.poolSize}</strong>
                      </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary">
                      <strong>Created:</strong>{' '}
                      {course.createdAt
                        ? new Date(course.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
                        : '—'}
                    </Typography>
                  </Box>
                </CardContent>
                <CardActions sx={{ px: 2, pb: 2, pt: 0, justifyContent: 'flex-end', gap: 1 }}>
                  <button
                    type="button"
                    title="Edit"
                    aria-label={`Edit ${course.title}`}
                    onClick={() => {
                      setError(null);
                      setEditing(course);
                    }}
                    style={iconBtn('#1976d2')}
                  >
                    ✏️
                  </button>
                  <button
                    type="button"
                    title="Delete"
                    aria-label={`Delete ${course.title}`}
                    onClick={async () => {
                      if (!window.confirm(`Delete course "${course.title}"?`)) return;
                      try {
                        setError(null);
                        await coursesApi.remove(course.id);
                        await load();
                      } catch (err: any) {
                        setError(err?.response?.data?.error || err.message);
                      }
                    }}
                    style={iconBtn('#d32f2f')}
                  >
                    🗑️
                  </button>
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default CoursesPage;
